import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Modal from './common/Modal';
import { toast } from './common/Toast';
import * as companiesApi from '../api/companies';
import { Company } from '../types';

type Mode = 'existing' | 'other' | 'unset';

interface CompanySelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (body: companiesApi.ChangeUserCompanyBody) => Promise<void>;
  currentCompanyId?: string | null;
  allowUnset?: boolean;
  title?: string;
  submitLabel?: string;
}

const errMsg = (err: unknown, fallback: string): string => {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { error?: string } | undefined;
    if (data?.error) return data.error;
  }
  return fallback;
};

const CompanySelectModal: React.FC<CompanySelectModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  currentCompanyId = null,
  allowUnset = true,
  title = '회사 선택',
  submitLabel = '저장',
}) => {
  const [mode, setMode] = useState<Mode>('existing');
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedId, setSelectedId] = useState<string>('');
  const [otherName, setOtherName] = useState('');
  const [dupStatus, setDupStatus] = useState<companiesApi.DuplicateCheckResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setMode('existing');
    setOtherName('');
    setDupStatus(null);
    setSelectedId(currentCompanyId || '');
    setLoading(true);
    companiesApi
      .getList({ limit: 200 })
      .then((res) => setCompanies(res.items))
      .catch(() => toast.error('회사 목록을 불러올 수 없습니다'))
      .finally(() => setLoading(false));
  }, [isOpen, currentCompanyId]);

  // 기타 입력 debounced 중복 검증
  useEffect(() => {
    if (mode !== 'other') return;
    if (!otherName.trim()) {
      setDupStatus(null);
      return;
    }
    const handle = setTimeout(() => {
      companiesApi
        .checkDuplicate(otherName.trim())
        .then((r) => setDupStatus(r))
        .catch(() => setDupStatus(null));
    }, 400);
    return () => clearTimeout(handle);
  }, [otherName, mode]);

  const dupWarning = useMemo(() => {
    if (mode !== 'other' || !otherName.trim() || !dupStatus?.exists) return null;
    if (dupStatus.is_approved) {
      return {
        level: 'error' as const,
        text: '이미 등록된 회사입니다. "기존 회사"에서 선택해주세요.',
      };
    }
    return {
      level: 'info' as const,
      text: '동일 이름의 승인 대기 중인 회사가 있습니다. 해당 그룹에 합류됩니다.',
    };
  }, [mode, otherName, dupStatus]);

  const canSubmit = useMemo(() => {
    if (submitting) return false;
    if (mode === 'unset') return allowUnset;
    if (mode === 'existing') return !!selectedId;
    if (mode === 'other') {
      const name = otherName.trim();
      if (!name) return false;
      // 이미 승인된 회사면 기타 입력 차단 (UX 가드)
      if (dupStatus?.exists && dupStatus.is_approved) return false;
      return true;
    }
    return false;
  }, [mode, selectedId, otherName, dupStatus, submitting, allowUnset]);

  const handleSubmit = useCallback(async () => {
    try {
      setSubmitting(true);
      if (mode === 'unset') {
        await onSubmit({ unset: true });
      } else if (mode === 'existing') {
        await onSubmit({ company_id: selectedId });
      } else if (mode === 'other') {
        await onSubmit({ company_name: otherName.trim() });
      }
      onClose();
    } catch (err) {
      toast.error(errMsg(err, '회사 변경에 실패했습니다'));
    } finally {
      setSubmitting(false);
    }
  }, [mode, selectedId, otherName, onSubmit, onClose]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      onConfirm={canSubmit ? handleSubmit : undefined}
      confirmText={submitting ? '처리 중...' : submitLabel}
    >
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <button
          type="button"
          className={`btn btn--sm ${mode === 'existing' ? 'btn--primary' : 'btn--secondary'}`}
          onClick={() => setMode('existing')}
        >
          기존 회사
        </button>
        <button
          type="button"
          className={`btn btn--sm ${mode === 'other' ? 'btn--primary' : 'btn--secondary'}`}
          onClick={() => setMode('other')}
        >
          기타 (직접 입력)
        </button>
        {allowUnset && (
          <button
            type="button"
            className={`btn btn--sm ${mode === 'unset' ? 'btn--primary' : 'btn--secondary'}`}
            onClick={() => setMode('unset')}
          >
            소속 해제 (외부자)
          </button>
        )}
      </div>

      {mode === 'existing' && (
        <div className="form-group">
          <label>회사 선택</label>
          {loading ? (
            <p>불러오는 중...</p>
          ) : companies.length === 0 ? (
            <p>등록된 회사가 없습니다. "기타" 로 직접 입력하세요.</p>
          ) : (
            <select value={selectedId} onChange={(e) => setSelectedId(e.target.value)}>
              <option value="">-- 선택 --</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      {mode === 'other' && (
        <>
          <div className="form-group">
            <label>회사명 직접 입력 (1~100자)</label>
            <input
              value={otherName}
              onChange={(e) => setOtherName(e.target.value)}
              maxLength={100}
              placeholder="회사명"
            />
          </div>
          {dupWarning && (
            <p
              style={{
                fontSize: 13,
                color: dupWarning.level === 'error' ? '#d32f2f' : '#1976d2',
                marginTop: -8,
              }}
            >
              {dupWarning.text}
            </p>
          )}
          <p style={{ fontSize: 12, color: '#666' }}>
            직접 입력한 회사는 승인 대기(pending) 상태로 저장되며, 관리자가 승인하기 전까지
            관리 페이지에서 "(승인 대기중)" 으로 표시됩니다.
          </p>
        </>
      )}

      {mode === 'unset' && (
        <p style={{ color: '#555' }}>
          소속을 해제하고 "외부자" 상태로 전환합니다. 이후 툴바의 "회사 선택" 버튼으로 다시 소속을
          지정할 수 있습니다.
        </p>
      )}
    </Modal>
  );
};

export default CompanySelectModal;
